import { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface Product {
  id: number
  description: string
  sale_price: number
  tracks_stock: boolean
  current_stock: number
}

interface Props {
  products: Product[]
  students: { id: number; name: string }[]
  errors?: Record<string, string>
}

const brl = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    value,
  )

export default function NewSale({ products, students, errors }: Props) {
  const [productId, setProductId] = useState<string>('')
  const [quantity, setQuantity] = useState('1')
  const [studentId, setStudentId] = useState<string>('')
  const [saving, setSaving] = useState(false)

  const product = useMemo(
    () => products.find((p) => String(p.id) === productId),
    [products, productId],
  )

  const qty = Math.max(parseInt(quantity, 10) || 0, 0)
  const total = product ? product.sale_price * qty : 0
  const semEstoque = product?.tracks_stock && qty > product.current_stock

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    router.post(
      '/sales',
      {
        sale: {
          product_id: productId,
          quantity: qty,
          student_id: studentId || undefined,
          description: product?.description,
        },
      },
      { onFinish: () => setSaving(false) },
    )
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-xl">
        <div className="mb-6">
          <h2 className="text-2xl font-bold">Nova venda</h2>
          <p className="text-sm text-muted-foreground">
            Venda de produto no balcão
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Produto</CardTitle>
            <CardDescription>
              O valor é o preço de tabela — o total é calculado no servidor.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-5">
              {errors?.base && (
                <p className="text-sm text-destructive">{errors.base}</p>
              )}

              <div className="space-y-2">
                <Label htmlFor="product">Produto *</Label>
                <Select value={productId} onValueChange={setProductId}>
                  <SelectTrigger id="product">
                    <SelectValue placeholder="Escolha o produto" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.description} — {brl(p.sale_price)}
                        {p.tracks_stock
                          ? ` (${p.current_stock} em estoque)`
                          : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 max-w-[140px]">
                <Label htmlFor="quantity">Quantidade *</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="student">Aluno (opcional)</Label>
                <Select value={studentId} onValueChange={setStudentId}>
                  <SelectTrigger id="student">
                    <SelectValue placeholder="Venda avulsa" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {product && (
                <div className="rounded-lg border p-4 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {qty} × {brl(product.sale_price)}
                  </span>
                  <span className="text-lg font-semibold">{brl(total)}</span>
                </div>
              )}

              {semEstoque && (
                <Badge variant="destructive">
                  Estoque insuficiente: {product?.current_stock} disponível
                </Badge>
              )}

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => router.visit('/sales')}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving || !product || qty < 1}>
                  {saving ? 'Registrando...' : 'Registrar venda'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
